import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Badge, InputGroup, Form, Spinner } from 'react-bootstrap';
import { FaSearch, FaGraduationCap, FaClock } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { getCourseImageUrl } from '../../utils/apiBase';
import api from '../../api/apiClient';
import { toast } from 'react-toastify';

const defaultCourseImage = 'https://via.placeholder.com/300x200/8b5cf6/ffffff?text=Course';

const InstructorCourseList = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    api.instructor.getCourses()
      .then((res) => {
        if (res.data?.success) {
          setCourses(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          setCourses([]);
        }
      })
      .catch((err) => toast.error('Failed to load courses: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, []);

  const isOrgInstructor = Boolean(user?.organization_id);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return courses;
    return courses.filter((c) =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.description || '').toLowerCase().includes(term)
    );
  }, [courses, searchTerm]);

  const formatDuration = (minutes) => {
    const value = parseInt(minutes, 10);
    if (!value || Number.isNaN(value)) return null;
    if (value < 60) return `${value} min`;
    const hours = Math.floor(value / 60);
    const mins = value % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
                <FaGraduationCap />
              </div>
              <div>
                <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">My Courses</h2>
                <p className="text-gray-600 text-sm sm:text-base m-0">
                  {isOrgInstructor
                    ? 'All courses assigned to your organization through course collaboration.'
                    : 'Courses directly assigned to you from course management.'}
                </p>
              </div>
            </div>
            {courses.length > 0 && (
              <Badge className="text-white bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 font-semibold rounded-lg sm:rounded-xl shadow-md text-xs sm:text-sm px-3 py-2">
                {courses.length} Course{courses.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6 mb-6">
        <InputGroup>
          <InputGroup.Text className="bg-white border-2 border-gray-200">
            <FaSearch className="text-purple-600" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-2 border-gray-200 focus:border-purple-500"
          />
        </InputGroup>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-12 sm:py-16">
            <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
            <p className="text-gray-600 text-base sm:text-lg font-medium mt-4">Loading courses...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaGraduationCap size={32} className="text-white" />
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {searchTerm ? 'No courses match your search' : 'No courses available'}
            </h5>
            <p className="text-gray-600 text-base sm:text-lg">
              {searchTerm ? 'Try a different search term.' : 'No courses have been assigned to your account yet.'}
            </p>
          </div>
        ) : (
          <Row className="g-4">
            {filtered.map((course) => {
              const imageUrl = course.course_image ? getCourseImageUrl(course.course_image) : defaultCourseImage;
              const duration = formatDuration(course.duration);
              const isActive = course.status === 1 || course.status === 'active';

              return (
                <Col key={course.id} md={6} lg={4} sm={12}>
                  <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <div style={{ height: '200px', overflow: 'hidden' }}>
                      <Card.Img
                        variant="top"
                        src={imageUrl}
                        alt={course.name}
                        style={{ height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.currentTarget.src = defaultCourseImage; }}
                      />
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <Card.Title className="fw-bold text-gray-900 mb-0 fs-6">{course.name}</Card.Title>
                        <Badge bg={course.source === 'organization' ? 'info' : 'primary'} className="text-xs flex-shrink-0">
                          {course.source === 'organization' ? 'Organization' : 'Assigned'}
                        </Badge>
                      </div>
                      <Card.Text className="text-muted small flex-grow-1" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {course.description || 'No description available.'}
                      </Card.Text>
                      <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
                        <Badge bg={isActive ? 'success' : 'secondary'} className="text-xs">
                          {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {duration && (
                          <span className="text-muted small d-flex align-items-center gap-1">
                            <FaClock className="text-purple-500" />
                            {duration}
                          </span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </div>
  );
};

export default InstructorCourseList;
